import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./LoginPage.css";

// Configuration de l'API
// Remplacer la configuration existante
const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL_PROD // https://demenagementsamyyhaul.ca/api
    : process.env.REACT_APP_API_URL_DEV; // http://158.69.205.231:3000

console.log("URL de l'API:", API_BASE_URL);
console.log("Endpoint d'authentification complet:", `${API_BASE_URL}/auth`);
const testServerConnection = async () => {
  try {
    console.log("Test de connexion au serveur...");
    const response = await axios.get(API_BASE_URL, {
      timeout: 5000,
      withCredentials: false, // Désactiver temporairement
    });
    console.log("Serveur accessible - Status:", response.status);
    return true;
  } catch (error) {
    console.error("Serveur inaccessible:", {
      code: error.code,
      message: error.message,
      url: API_BASE_URL,
    });
    return false;
  }
};
// Configuration axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
  withCredentials: false,
});

// Intercepteur pour les réponses d'erreur
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("Erreur API détaillée:", {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      url: error.config?.url,
      requestData: error.config?.data, // Ajout des données de la requête
    });

    if (error.code === "ECONNABORTED") {
      return Promise.reject(new Error("Délai d'attente dépassé"));
    }
    if (error.code === "ECONNREFUSED") {
      return Promise.reject(new Error("Impossible de se connecter au serveur"));
    }
    if (error.response?.status === 500) {
      return Promise.reject(new Error("Erreur serveur interne"));
    }
    return Promise.reject(error);
  }
);

function LoginPage() {
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  // Animation des particules de fond
  useEffect(() => {
    const createParticle = () => {
      const particle = document.createElement("div");
      particle.className = "particle";
      particle.style.left = Math.random() * 100 + "%";
      particle.style.animationDuration = Math.random() * 3 + 2 + "s";
      particle.style.opacity = Math.random() * 0.5 + 0.1;
      document.querySelector(".particles-container")?.appendChild(particle);

      setTimeout(() => {
        particle.remove();
      }, 5000);
    };

    const particleInterval = setInterval(createParticle, 300);
    return () => clearInterval(particleInterval);
  }, []);

  // API Functions
  const loginUser = async (credentials) => {
    try {
      console.log("Tentative de connexion avec:", credentials);

      // Test de connexion au serveur
      const serverAvailable = await testServerConnection();
      if (!serverAvailable) {
        throw new Error(
          `Serveur backend inaccessible à l'adresse: ${API_BASE_URL}`
        );
      }

      // Essayer différents endpoints possibles
      let response;
      const endpointsToTry = ["/auth", "/auth/login", "/login", "/api/auth"];

      for (const endpoint of endpointsToTry) {
        try {
          console.log(`Essai avec l'endpoint: ${endpoint}`);
          response = await apiClient.post(
            endpoint,
            {
              email: credentials.email,
              password: credentials.password,
            },
            {
              timeout: 10000,
              validateStatus: (status) => status < 500,
            }
          );

          // Si on obtient une réponse valide, sortir de la boucle
          if (response.status !== 404) {
            console.log(
              `Endpoint ${endpoint} réussi - Status:`,
              response.status
            );
            break;
          }
        } catch (endpointError) {
          console.log(`Endpoint ${endpoint} a échoué:`, endpointError.message);
          continue;
        }
      }

      // Si aucun endpoint n'a fonctionné
      if (!response || response.status === 404) {
        throw new Error(
          `Aucun endpoint d'authentification trouvé. Endpoints testés: ${endpointsToTry.join(
            ", "
          )}`
        );
      }

      console.log("Réponse du serveur:", {
        status: response.status,
        data: response.data,
        headers: response.headers,
      });

      // Gérer les réponses d'erreur
      if (response.status === 401) {
        throw new Error("Identifiants incorrects");
      }

      if (response.status === 400) {
        throw new Error(response.data?.message || "Données invalides");
      }

      if (response.status >= 300) {
        throw new Error(
          `Erreur serveur (${response.status}): ${
            response.data?.message || "Erreur inconnue"
          }`
        );
      }

      // Vérifier la présence du token
      const responseData = response.data;
      const token =
        responseData.token ||
        responseData.accessToken ||
        responseData.access_token;

      if (!token) {
        console.error("Structure de réponse inattendue:", responseData);
        throw new Error("Token manquant dans la réponse du serveur");
      }

      const userData =
        responseData.user || responseData.userData || responseData;

      return { token, user: userData };
    } catch (error) {
      console.error("Erreur de connexion détaillée:", {
        name: error.name,
        message: error.message,
        code: error.code,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url,
      });

      // Relancer l'erreur avec un message amélioré
      if (error.response) {
        switch (error.response.status) {
          case 401:
            throw new Error("Email ou mot de passe incorrect");
          case 404:
            throw new Error(
              "Service d'authentification non trouvé. Contactez l'administrateur."
            );
          case 400:
            throw new Error(
              error.response.data.message || "Données de connexion invalides"
            );
          case 500:
            throw new Error("Erreur interne du serveur. Réessayez plus tard.");
          default:
            throw new Error(`Erreur serveur (${error.response.status})`);
        }
      } else if (error.code === "ECONNABORTED") {
        throw new Error(
          "Délai d'attente dépassé. Le serveur met trop de temps à répondre."
        );
      } else if (
        error.code === "ECONNREFUSED" ||
        error.message.includes("ENOTFOUND")
      ) {
        throw new Error(
          `Impossible de se connecter au serveur: ${API_BASE_URL}`
        );
      } else {
        throw error;
      }
    }
  };
  const registerUser = async (userData) => {
    try {
      console.log("Tentative d'inscription avec:", userData);

      // Payload simplifié et correct
      const payload = {
        firstName: userData.firstName.trim(),
        lastName: userData.lastName.trim(),
        email: userData.email.trim().toLowerCase(),
        password: userData.password,
        role: "USER",
      };

      console.log("Payload envoyé à /users:", payload);

      // CORRECTION : Utiliser UNIQUEMENT l'endpoint /users avec gestion d'erreur améliorée
      const response = await apiClient.post("/users", payload, {
        validateStatus: function (status) {
          // Accepter tous les statuts pour pouvoir lire les messages d'erreur
          return true; // Accepter tous les statuts
        },
        timeout: 10000,
      });

      console.log("Réponse complète du serveur:", {
        status: response.status,
        data: response.data,
        headers: response.headers,
      });

      // CORRECTION : Analyser la réponse selon le statut
      if (response.status === 201) {
        console.log("✅ Inscription réussie !");
        return response.data;
      }

      // Extraire le message d'erreur
      const errorData = response.data;
      const errorMessage =
        errorData?.error || errorData?.message || JSON.stringify(errorData);

      console.log("📋 Message d'erreur du serveur:", errorMessage);

      // Gestion spécifique selon le statut
      switch (response.status) {
        case 409:
          // Email déjà existant
          throw new Error(
            "Cet email est déjà utilisé. Veuillez utiliser un autre email ou vous connecter."
          );

        case 400:
          // Données invalides
          if (
            errorMessage.toLowerCase().includes("password") ||
            errorMessage.toLowerCase().includes("mot de passe")
          ) {
            throw new Error(
              "Le mot de passe ne respecte pas les exigences de sécurité. Il doit contenir au moins 6 caractères avec des lettres et des chiffres."
            );
          } else if (errorMessage.toLowerCase().includes("email")) {
            throw new Error("Format d'email invalide");
          } else if (errorMessage.toLowerCase().includes("firstname")) {
            throw new Error("Le prénom est requis");
          } else if (errorMessage.toLowerCase().includes("lastname")) {
            throw new Error("Le nom est requis");
          } else {
            throw new Error("Données de formulaire invalides: " + errorMessage);
          }

        case 401:
          // Erreur d'authentification - probablement mot de passe trop faible
          if (
            errorMessage.toLowerCase().includes("password") ||
            errorMessage.toLowerCase().includes("mot de passe")
          ) {
            throw new Error(
              "Le mot de passe est trop faible. Il doit contenir au moins 8 caractères avec des lettres majuscules, minuscules et des chiffres."
            );
          } else {
            throw new Error("Erreur de sécurité: " + errorMessage);
          }

        case 422:
          // Erreur de validation
          throw new Error("Données de formulaire incorrectes: " + errorMessage);

        default:
          // Autres erreurs
          throw new Error(
            `Erreur lors de l'inscription (${response.status}): ${errorMessage}`
          );
      }
    } catch (error) {
      console.error("❌ Erreur d'inscription complète:", {
        name: error.name,
        message: error.message,
        responseStatus: error.response?.status,
        responseData: error.response?.data,
      });

      // Si l'erreur a déjà un message personnalisé, la propager
      if (
        error.message &&
        (error.message.includes("email est déjà utilisé") ||
          error.message.includes("mot de passe") ||
          error.message.includes("Format d'email") ||
          error.message.includes("prénom est requis") ||
          error.message.includes("nom est requis"))
      ) {
        throw error;
      }

      // Gestion des erreurs réseau
      if (error.code === "ECONNABORTED") {
        throw new Error("Délai d'attente dépassé. Veuillez réessayer.");
      }
      if (error.code === "ECONNREFUSED") {
        throw new Error(
          "Impossible de se connecter au serveur. Vérifiez votre connexion internet."
        );
      }

      // Si c'est une erreur axios avec réponse
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        const serverMessage =
          errorData?.error || errorData?.message || "Erreur inconnue";

        switch (status) {
          case 409:
            throw new Error("Cet email est déjà utilisé");
          case 400:
            throw new Error("Données invalides: " + serverMessage);
          case 401:
            throw new Error("Mot de passe trop faible: " + serverMessage);
          default:
            throw new Error(`Erreur serveur (${status}): ${serverMessage}`);
        }
      }

      // Erreur par défaut
      throw new Error(
        error.message ||
          "Erreur lors de la création du compte. Veuillez réessayer."
      );
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (isSignUp) {
      // Validation du prénom
      if (!firstName.trim()) {
        newErrors.firstName = "Le prénom est requis";
      } else if (firstName.trim().length < 2) {
        newErrors.firstName = "Minimum 2 caractères";
      } else if (firstName.trim().length > 50) {
        newErrors.firstName = "Maximum 50 caractères";
      }

      // Validation du nom
      if (!lastName.trim()) {
        newErrors.lastName = "Le nom de famille est requis";
      } else if (lastName.trim().length < 2) {
        newErrors.lastName = "Minimum 2 caractères";
      } else if (lastName.trim().length > 50) {
        newErrors.lastName = "Maximum 50 caractères";
      }
    }

    // Validation de l'email (toujours requis)
    if (!email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = "Format d'email invalide";
    } else if (email.trim().length > 100) {
      newErrors.email = "Email trop long (max 100 caractères)";
    }

    // Validation du mot de passe
    if (!password.trim()) {
      newErrors.password = "Le mot de passe est requis";
    }

    if (isSignUp) {
      // Validation renforcée du mot de passe pour l'inscription
      if (password.length < 6) {
        newErrors.password = "Minimum 6 caractères requis";
      } else if (password.length > 128) {
        newErrors.password = "Maximum 128 caractères";
      } else if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(password)) {
        newErrors.password =
          "Le mot de passe doit contenir des lettres et des chiffres";
      }

      // Validation de la confirmation du mot de passe
      if (!confirmPassword) {
        newErrors.confirmPassword = "Veuillez confirmer le mot de passe";
      } else if (password !== confirmPassword) {
        newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      if (isSignUp) {
        // INSCRIPTION - Section améliorée avec gestion guest
        const userData = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim().toLowerCase(),
          password: password,
        };

        console.log("🚀 Début de l'inscription...");

        // Étape 1: Création du compte
        const result = await registerUser(userData);
        console.log("✅ Compte créé avec succès:", result);

        // Étape 2: Liaison des demandes guest SI l'utilisateur est créé
        if (result && result.user && result.user.id) {
          console.log("🎫 Vérification des demandes guest à lier...");
          const linkResult = await linkGuestToUser(result.user.id);

          // Afficher un message informatif à l'utilisateur
          if (linkResult.success) {
            if (linkResult.data?.linkedCount > 0) {
              console.log(
                `✅ ${linkResult.data.linkedCount} demande(s) liée(s)`
              );
              // Stocker cette information pour l'afficher après la connexion
              localStorage.setItem(
                "guestLinkMessage",
                `Vos ${linkResult.data.linkedCount} demande(s) précédente(s) ont été liées à votre compte.`
              );
            }
          } else {
            console.warn("⚠️ Liaison partielle:", linkResult.message);
          }
        }

        // Succès de l'inscription
        setIsSignUp(false);
        setErrors({
          success:
            "Compte créé avec succès ! Veuillez vous connecter." +
            (result?.linkedCount
              ? ` ${result.linkedCount} demande(s) précédente(s) liée(s).`
              : ""),
        });
        resetForm();
      } else {
        // CONNEXION - Section existante améliorée
        const credentials = {
          email: email.trim().toLowerCase(),
          password: password,
        };

        console.log("🔑 Tentative de connexion...");
        const result = await loginUser(credentials);

        if (!result || !result.token) {
          throw new Error("Réponse invalide du serveur: token manquant");
        }

        // Stocker les informations utilisateur
        localStorage.setItem("authToken", result.token);
        localStorage.setItem(
          "currentUser",
          JSON.stringify(result.user || { email })
        );

        // Stocker l'ID utilisateur séparément
        if (result.user && result.user.id) {
          localStorage.setItem("userId", result.user.id.toString());
        }

        // Configuration de l'axios avec le token
        apiClient.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${result.token}`;

        // Vérifier s'il y a un message de liaison guest à afficher
        const guestLinkMessage = localStorage.getItem("guestLinkMessage");
        if (guestLinkMessage) {
          console.log("📢 Message de liaison:", guestLinkMessage);
          // Vous pouvez stocker ce message pour l'afficher sur la page de destination
          localStorage.setItem("displayMessage", guestLinkMessage);
          localStorage.removeItem("guestLinkMessage");
        }

        // Redirection selon le rôle
        const userRole = (
          result.user?.role ||
          result.role ||
          "USER"
        ).toUpperCase();
        console.log(
          `🎯 Redirection vers: ${userRole === "ADMIN" ? "/admin" : "/user"}`
        );

        if (userRole === "ADMIN") {
          navigate("/admin");
        } else {
          navigate("/user");
        }
      }
    } catch (error) {
      console.error("❌ Erreur d'authentification:", error);

      if (error.validationErrors) {
        setErrors(error.validationErrors);
      } else {
        setErrors({ general: error.message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setErrors({});
    resetForm();
  };

  const resetForm = () => {
    setLastName("");
    setPassword("");
    setConfirmPassword("");
    setEmail("");
    setFirstName("");
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const clearError = (field) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Vérifier si l'utilisateur est déjà connecté
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const currentUser = localStorage.getItem("currentUser");

    if (token && currentUser) {
      try {
        const user = JSON.parse(currentUser);
        apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const userRole = (user.role || "USER").toUpperCase();
        if (userRole === "ADMIN") {
          navigate("/admin");
        } else {
          navigate("/user");
        }
      } catch (error) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("currentUser");
      }
    }
  }, [navigate]);
  const [passwordSection, setPasswordSection] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [resetPasswordData, setResetPasswordData] = useState({
    email: "",
    verificationCode: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordMessage, setPasswordMessage] = useState("");
  const [isPasswordSuccess, setIsPasswordSuccess] = useState(false);
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);

  // Fonction pour envoyer le code de vérification
  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      setPasswordMessage("Veuillez entrer votre adresse email");
      setIsPasswordSuccess(false);
      return;
    }

    try {
      setIsLoadingPassword(true);
      setPasswordMessage("");

      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      });

      if (response.status === 201) {
        setPasswordMessage("✅ Code de vérification envoyé à votre email");
        setIsPasswordSuccess(true);
        // Pré-remplir l'email dans le formulaire de reset
        setResetPasswordData((prev) => ({
          ...prev,
          email: forgotPasswordEmail,
        }));
      } else {
        const errorData = await response.json();
        setPasswordMessage(
          `❌ ${errorData.message || "Erreur lors de l'envoi du code"}`
        );
        setIsPasswordSuccess(false);
      }
    } catch (error) {
      console.error("Erreur forgot password:", error);
      setPasswordMessage("❌ Erreur de connexion au serveur");
      setIsPasswordSuccess(false);
    } finally {
      setIsLoadingPassword(false);
    }
  };
  const [userProfile, setUserProfile] = useState({
    id: "",
    name: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    profilPicture: "",
    userStatus: "",
    role: "",
    mailStatus: false,
  });
  // Fonction pour réinitialiser le mot de passe
  const handleResetPassword = async () => {
    // Validation
    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      setPasswordMessage("❌ Les mots de passe ne correspondent pas");
      setIsPasswordSuccess(false);
      return;
    }

    if (resetPasswordData.newPassword.length < 6) {
      setPasswordMessage(
        "❌ Le mot de passe doit contenir au moins 6 caractères"
      );
      setIsPasswordSuccess(false);
      return;
    }

    try {
      setIsLoadingPassword(true);
      setPasswordMessage("");

      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: resetPasswordData.email,
          verificationCode: resetPasswordData.verificationCode,
          newPassword: resetPasswordData.newPassword,
        }),
      });

      if (response.ok) {
        setPasswordMessage("✅ Mot de passe réinitialisé avec succès !");
        setIsPasswordSuccess(true);
        // Réinitialiser le formulaire
        setResetPasswordData({
          email: "",
          verificationCode: "",
          newPassword: "",
          confirmPassword: "",
        });
        setForgotPasswordEmail("");

        // Fermer la section après 2 secondes
        setTimeout(() => {
          setPasswordSection(false);
        }, 2000);
      } else {
        const errorData = await response.json();
        setPasswordMessage(
          `❌ ${errorData.message || "Erreur lors de la réinitialisation"}`
        );
        setIsPasswordSuccess(false);
      }
    } catch (error) {
      console.error("Erreur reset password:", error);
      setPasswordMessage("❌ Erreur de connexion au serveur");
      setIsPasswordSuccess(false);
    } finally {
      setIsLoadingPassword(false);
    }
  };

  // Fonction pour gérer les changements dans le formulaire
  const handlePasswordChange = (field, value) => {
    setResetPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (userProfile.email) {
      setForgotPasswordEmail(userProfile.email);
      setResetPasswordData((prev) => ({
        ...prev,
        email: userProfile.email,
      }));
    }
  }, [userProfile.email]);

  // Fonction pour formater automatiquement le code de vérification
  const handleVerificationCodeChange = (value) => {
    // N'autoriser que les chiffres
    const numericValue = value.replace(/\D/g, "");
    // Limiter à 6 chiffres
    const formattedValue = numericValue.slice(0, 6);
    handlePasswordChange("verificationCode", formattedValue);
  };
  // Dans LoginPage.js, ajoutez cette fonction après les autres fonctions API
  // Fonction améliorée pour lier les demandes guest à l'utilisateur
  // Fonction améliorée dans LoginPage.js
  const linkGuestToUser = async (userId) => {
    try {
      const guestToken = localStorage.getItem("guestToken");

      if (!guestToken) {
        console.log("🎫 Aucun token guest trouvé");
        return { success: true, linkedCount: 0 };
      }

      console.log("🔄 Liaison des demandes guest...");

      const response = await apiClient.post("/demandes/link-to-user", {
        guestToken,
        userId: userId.toString(),
      });

      if (response.data.success) {
        const linkedCount = response.data.data?.linkedCount || 0;

        // Nettoyage immédiat après succès
        localStorage.removeItem("guestToken");

        return {
          success: true,
          linkedCount,
          message:
            linkedCount > 0
              ? `Vos ${linkedCount} demande(s) précédente(s) ont été liées à votre compte`
              : null,
        };
      }

      return response.data;
    } catch (error) {
      console.error("❌ Erreur liaison:", error);
      // Ne pas bloquer l'utilisateur
      return {
        success: false,
        linkedCount: 0,
        error: error.message,
      };
    }
  };
  // Fetch company config pour le logo
  useEffect(() => {
    fetch(`${API_BASE_URL}/company-config/latest`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        console.log("Company config reçu dans LoginPage:", data);

        const updatedData = {
          ...data,
          logo: data.logo.includes("example.com")
            ? "/uploads/company-logos/logo-removebg-preview (1).png"
            : data.logo,
        };

        setCompany(updatedData);
        setLogoError(false);
      })
      .catch((err) => {
        console.error(
          "Erreur récupération company-config dans LoginPage:",
          err
        );
      });
  }, []);
  const [company, setCompany] = useState(null);
  const [logoError, setLogoError] = useState(false);
  // Fonction pour obtenir l'URL du logo (identique à celle du header)
  const getLogoUrl = (logoPath) => {
    const uploadedLogo = localStorage.getItem("uploadedLogoUrl");

    if (uploadedLogo) {
      return uploadedLogo.startsWith("http")
        ? uploadedLogo
        : `${API_BASE_URL}${uploadedLogo}`;
    }

    if (!logoPath) {
      return "/default-logo.png";
    }

    if (logoPath.startsWith("http://") || logoPath.startsWith("https://")) {
      return logoPath.includes("example.com") ? "/default-logo.png" : logoPath;
    }

    const cleanPath = logoPath.startsWith("/") ? logoPath : `/${logoPath}`;
    return `${API_BASE_URL}${cleanPath}`;
  };

  const handleImageError = (e) => {
    console.error("Erreur de chargement du logo dans LoginPage:", e.target.src);
    setLogoError(true);
  };

  const handleImageLoad = () => {
    console.log("Logo chargé avec succès dans LoginPage:", company?.logo);
    setLogoError(false);
  };

  return (
    <div className="login-page">
      <div className="particles-container"></div>

      <div className="login-container">
        {/* Left Panel - Branding */}
        <div className="brand-panel">
          <div className="brand-content">
            <div className="logo-container">
              <a href="#" className="flex items-center space-x-3 mb-6">
                {company?.logo && (
                  <img
                    src={
                      company.logo?.startsWith("http")
                        ? company.logo
                        : `${API_BASE_URL}${
                            company.logo || "/default-logo.png"
                          }`
                    }
                    alt={company?.name || "Company Logo"}
                    className="w-30 h-30 md:w-40 md:h-40 object-contain rounded-lg border-4 border-white p-2 bg-white shadow-lg"
                  />
                )}
                <br />
                <span className="text-6xl font-bold text-red-600">
                  {company?.name || "Chargement..."}
                </span>
              </a>
            </div>

            <div className="brand-message">
              <h1 className="brand-title">
                {isSignUp
                  ? "Rejoignez notre communauté"
                  : "Bon retour parmi nous"}
              </h1>
              <p className="brand-subtitle">
                {isSignUp
                  ? "Créez votre compte et découvrez nos services de déménagement premium à Montréal"
                  : "Connectez-vous pour accéder à votre espace personnel et gérer vos services"}
              </p>
            </div>

            <div className="brand-features">
              <div className="feature">
                <span className="feature-icon">✨</span>
                <span>Service Premium</span>
              </div>
              <div className="feature">
                <span className="feature-icon">🛡️</span>
                <span>Sécurisé & Fiable</span>
              </div>
              <div className="feature">
                <span className="feature-icon">⚡</span>
                <span>Support 24/7</span>
              </div>
            </div>

            <div className="decorative-shapes">
              <div className="shape shape-1"></div>
              <div className="shape shape-2"></div>
              <div className="shape shape-3"></div>
            </div>
          </div>
        </div>
        {/* Right Panel - Form */}
        <div className="form-panel">
          <div className="form-content">
            {/* Toggle Buttons */}
            <div className="mode-toggle">
              <button
                type="button"
                className={`toggle-button ${!isSignUp ? "active" : ""}`}
                onClick={() => !isSignUp || toggleMode()}
              >
                <span className="toggle-icon">🔑</span>
                <span>Connexion</span>
              </button>
              <button
                type="button"
                className={`toggle-button ${isSignUp ? "active" : ""}`}
                onClick={() => isSignUp || toggleMode()}
              >
                <span className="toggle-icon">👤</span>
                <span>Inscription</span>
              </button>
            </div>

            {/* Form */}
            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="form-header">
                <h2 className="form-title">
                  {isSignUp ? "Créer un compte" : "Se connecter"}
                </h2>
                <p className="form-description">
                  {isSignUp
                    ? "Remplissez les informations ci-dessous"
                    : "Entrez vos identifiants pour continuer"}
                </p>
              </div>

              {errors.general && (
                <div className="alert alert-error">
                  <span className="alert-icon">⚠️</span>
                  {errors.general}
                </div>
              )}

              {errors.success && (
                <div className="alert alert-success">
                  <span className="alert-icon">✅</span>
                  {errors.success}
                </div>
              )}

              <div className="form-fields">
                {isSignUp && (
                  <div className="input-group">
                    <div className="input-wrapper">
                      <input
                        type="text"
                        id="firstName"
                        value={firstName}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                          clearError("firstName");
                        }}
                        className={`form-input ${
                          errors.firstName ? "error" : ""
                        } ${firstName ? "filled" : ""}`}
                        placeholder=" "
                        autoComplete="given-name"
                        maxLength={50}
                      />
                      <label htmlFor="firstName" className="form-label">
                        <span className="label-icon">👨‍💼</span>
                        Prénom
                      </label>
                    </div>
                    {errors.firstName && (
                      <span className="error-message">{errors.firstName}</span>
                    )}
                  </div>
                )}

                {isSignUp && (
                  <div className="input-group">
                    <div className="input-wrapper">
                      <input
                        type="text"
                        id="lastName"
                        value={lastName}
                        onChange={(e) => {
                          setLastName(e.target.value);
                          clearError("lastName");
                        }}
                        className={`form-input ${
                          errors.lastName ? "error" : ""
                        } ${lastName ? "filled" : ""}`}
                        placeholder=" "
                        autoComplete="family-name"
                        maxLength={50}
                      />
                      <label htmlFor="lastName" className="form-label">
                        <span className="label-icon">👤</span>
                        Nom de famille
                      </label>
                    </div>
                    {errors.lastName && (
                      <span className="error-message">{errors.lastName}</span>
                    )}
                  </div>
                )}

                <div className="input-group">
                  <div className="input-wrapper">
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearError("email");
                      }}
                      className={`form-input ${errors.email ? "error" : ""} ${
                        email ? "filled" : ""
                      }`}
                      placeholder=" "
                      autoComplete="email"
                      maxLength={100}
                    />
                    <label htmlFor="email" className="form-label">
                      <span className="label-icon">📧</span>
                      Adresse email
                    </label>
                  </div>
                  {errors.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </div>

                <div className="input-group">
                  <div className="input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        clearError("password");
                      }}
                      className={`form-input ${
                        errors.password ? "error" : ""
                      } ${password ? "filled" : ""}`}
                      placeholder=" "
                      autoComplete={
                        isSignUp ? "new-password" : "current-password"
                      }
                      maxLength={128}
                    />
                    <label htmlFor="password" className="form-label">
                      <span className="label-icon">🔒</span>
                      Mot de passe
                    </label>
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? "👁️" : "👁️‍🗨️"}
                    </button>
                  </div>
                  {errors.password && (
                    <span className="error-message">{errors.password}</span>
                  )}

                  {/* Ajout des conseils de mot de passe */}
                  {isSignUp && password && (
                    <div className="password-strength-hints">
                      <div
                        className={`hint ${
                          password.length >= 6 ? "valid" : ""
                        }`}
                      >
                        {password.length >= 6 ? "✅" : "❌"} Au moins 6
                        caractères
                      </div>
                      <div
                        className={`hint ${
                          /(?=.*[a-zA-Z])/.test(password) ? "valid" : ""
                        }`}
                      >
                        {/(?=.*[a-zA-Z])/.test(password) ? "✅" : "❌"} Contient
                        des lettres
                      </div>
                      <div
                        className={`hint ${
                          /(?=.*\d)/.test(password) ? "valid" : ""
                        }`}
                      >
                        {/(?=.*\d)/.test(password) ? "✅" : "❌"} Contient des
                        chiffres
                      </div>
                    </div>
                  )}
                </div>

                {isSignUp && (
                  <div className="input-group">
                    <div className="input-wrapper">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          clearError("confirmPassword");
                        }}
                        className={`form-input ${
                          errors.confirmPassword ? "error" : ""
                        } ${confirmPassword ? "filled" : ""}`}
                        placeholder=" "
                        autoComplete="new-password"
                        maxLength={128}
                      />
                      <label htmlFor="confirmPassword" className="form-label">
                        <span className="label-icon">🔐</span>
                        Confirmer le mot de passe
                      </label>
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <span className="error-message">
                        {errors.confirmPassword}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className={`submit-button ${isLoading ? "loading" : ""}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    <span>Traitement...</span>
                  </>
                ) : (
                  <>
                    <span className="button-icon">
                      {isSignUp ? "🚀" : "🔓"}
                    </span>
                    <span>
                      {isSignUp ? "Créer mon compte" : "Se connecter"}
                    </span>
                  </>
                )}
              </button>
              {/* Section de changement de mot de passe */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-800">
                    mot de passe oublié ?
                  </h3>
                  <button
                    onClick={() => setPasswordSection(!passwordSection)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {passwordSection ? "Masquer" : "Afficher"}
                  </button>
                </div>

                {passwordSection && (
                  <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
                    {!isPasswordSuccess ? (
                      <>
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Email pour réinitialisation
                          </label>
                          <input
                            type="email"
                            value={forgotPasswordEmail}
                            onChange={(e) =>
                              setForgotPasswordEmail(e.target.value)
                            }
                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Votre adresse email"
                          />
                        </div>

                        <button
                          onClick={handleForgotPassword}
                          disabled={isLoadingPassword}
                          className="px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 mb-6"
                        >
                          {isLoadingPassword
                            ? "Envoi en cours..."
                            : "Envoyer le code de vérification"}
                        </button>
                      </>
                    ) : null}

                    {isPasswordSuccess && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Code de vérification
                            </label>
                            <input
                              type="text"
                              value={resetPasswordData.verificationCode}
                              onChange={(e) =>
                                handleVerificationCodeChange(e.target.value)
                              }
                              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Code à 6 chiffres"
                              maxLength={6}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Nouveau mot de passe
                            </label>
                            <input
                              type="password"
                              value={resetPasswordData.newPassword}
                              onChange={(e) =>
                                handlePasswordChange(
                                  "newPassword",
                                  e.target.value
                                )
                              }
                              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Minimum 6 caractères"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Confirmer le mot de passe
                          </label>
                          <input
                            type="password"
                            value={resetPasswordData.confirmPassword}
                            onChange={(e) =>
                              handlePasswordChange(
                                "confirmPassword",
                                e.target.value
                              )
                            }
                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Retapez votre mot de passe"
                          />
                        </div>

                        <div className="flex items-center space-x-4">
                          <button
                            onClick={handleResetPassword}
                            disabled={isLoadingPassword}
                            className="px-6 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50"
                          >
                            {isLoadingPassword
                              ? "Traitement..."
                              : "Réinitialiser le mot de passe"}
                          </button>

                          <button
                            onClick={() => {
                              setPasswordSection(false);
                              setPasswordMessage("");
                              setIsPasswordSuccess(false);
                            }}
                            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}

                    {passwordMessage && (
                      <div
                        className={`mt-4 p-3 rounded-lg text-sm font-medium ${
                          isPasswordSuccess
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-red-100 text-red-800 border border-red-200"
                        }`}
                      >
                        {passwordMessage}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="form-footer">
                <p className="switch-mode">
                  {isSignUp ? "Déjà un compte?" : "Pas encore de compte?"}
                  <button
                    type="button"
                    className="link-button"
                    onClick={toggleMode}
                  >
                    {isSignUp ? "Se connecter" : "S'inscrire"}
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
